import type { Directive } from 'vue'
import { router } from '@/router'
import { ytWebTracking } from './web-Tracking'

/**
 * 点击埋点指令 v-yt-push（独立模块）
 * 主应用在 src/utils/directive.ts 中注册；通过 createApp 动态挂载的独立弹窗应用
 * （login / AiFeedback / CoursePayment / PdfExpro / word-detail-dialog / workProgress 等）
 * 与主应用 app context 隔离，主应用注册的全局指令对它们不生效，需各自单独注册本指令。
 * 注意：不要放进 directive.ts 再让 login 等模块引用——directive.ts 的 auth-login 指令依赖
 * login 组件，会形成循环依赖。
 * 实现说明：不在每个元素上挂 click 监听——在 el-dialog + Transition 等元素频繁重挂载的
 * 场景下，mounted 阶段挂上的监听可能落在已被替换的旧节点上，真实点击会漏报。
 * 改为 mounted/updated 时把 btnName 同步写入元素的 data-yt-push 属性（随 DOM patch
 * 同步生效，不依赖监听器存活），由模块级唯一的 document 捕获阶段委托监听器统一上报；
 * router_path / page_name 在点击时取当前路由，避免闭包捕获过期的路由快照。
 * 捕获阶段在目标元素任何 stopPropagation 之前触发，与旧实现相比不会被内部拦截吞掉。
 */
let delegateInstalled = false

function installClickDelegate() {
  if (delegateInstalled) return
  delegateInstalled = true
  document.addEventListener(
    'click',
    (e) => {
      const target = e.target as Element | null
      const el = target?.closest?.('[data-yt-push]')
      if (!(el instanceof HTMLElement)) return
      ytWebTracking.click({
        eventType: 'common_click',
        router_path: router.currentRoute.value.path,
        page_name: (router.currentRoute.value?.meta?.title || '') as string,
        btn_name: el.dataset.ytPush || el.innerText || ''
      })
    },
    true
  )
}

function syncBtnName(el: HTMLElement, btnName: string | undefined) {
  // 无 btnName 时写空串标记：委托监听器靠 [data-yt-push] 识别元素，
  // 上报时再回落到 innerText，保持「裸 v-yt-push 取元素文本」的旧语义
  el.dataset.ytPush = btnName || ''
}

export const ytPushDirective: Directive<HTMLElement, { btnName?: string }> = {
  mounted(el, binding) {
    installClickDelegate()
    syncBtnName(el, binding?.value?.btnName)
  },
  updated(el, binding) {
    syncBtnName(el, binding?.value?.btnName)
  },
  beforeUnmount(el) {
    el.removeAttribute('data-yt-push')
  }
}
